// Shared lead-capture helpers used by the quote form, callback box and exit popup.
// The Base44 SDK and email templates are loaded only when an alert is actually sent,
// so they never slow down the page for visitors who are just browsing.
import { getBase44 } from '@/api/base44Lazy';
import { getService } from '@/lib/pricing';

// Who gets callback-request alerts.
export const CALLBACK_TO = 'info@daleyinventories.com';
export const CALLBACK_CC = 'victor@daleyinventories.com';
export const CALLBACK_MINUTES = 15;

export const isValidEmail = e => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((e || '').trim());
export const isValidPhone = p => (p || '').replace(/\D/g, '').length >= 10;

// Split one "email or mobile" box into the right field.
export function parseContact(value) {
  const v = (value || '').trim();
  if (isValidEmail(v)) return { email: v, phone: '' };
  if (isValidPhone(v)) return { email: '', phone: v };
  return null;
}

// Remember on this device that the visitor has already given us their details,
// so we never nag them with the exit popup.
const KEY = 'daley_lead_captured';
export function markLeadCaptured() {
  try { localStorage.setItem(KEY, String(Date.now())); } catch { /* storage blocked */ }
}
export function hasCapturedLead() {
  try { return !!localStorage.getItem(KEY); } catch { return false; }
}

// Branded "call this person back within 15 minutes" alert to the office (Victor in CC).
export async function sendCallbackAlert(data) {
  const services = (data.service_ids || []).map(id => getService(id)?.name || id).join(', ');
  try {
    const [base44, { buildCallbackEmailHtml }] = await Promise.all([getBase44(), import('@/lib/emailTemplates')]);
    await base44.functions.invoke('send-notification', {
      to: CALLBACK_TO,
      cc: CALLBACK_CC,
      subject: `📞 CALLBACK REQUEST — call within ${CALLBACK_MINUTES} mins — ${data.customer_name || data.phone}`,
      body: [
        `Callback request — please call within ${CALLBACK_MINUTES} minutes.`,
        '',
        `Name: ${data.customer_name || 'Not given'}`,
        `Phone: ${data.phone}`,
        `Email: ${data.email || 'Not given'}`,
        `Services: ${services || 'Not sure yet'}`,
        `Postcode: ${data.postcode || 'Not given'}`,
      ].join('\n'),
      html: buildCallbackEmailHtml(data),
      ...(data.email ? { replyTo: data.email } : {}),
    });
  } catch (err) {
    console.error('Callback alert failed:', err?.message);
  }
}

// Email the team about a quick lead (callback / exit popup).
export async function notifyTeam({ subject, lines }) {
  try {
    const base44 = await getBase44();
    await base44.functions.invoke('send-notification', {
      to: 'enquiries@daleyinventories.com',
      subject,
      body: lines.filter(Boolean).join('\n'),
    });
  } catch (err) {
    console.error('Team notification failed:', err?.message);
  }
}
